import React, { useState, useRef, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import { UitkCard } from 'uitk-react-cards';
import { UitkHeading } from 'uitk-react-text';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';
import useLocale from '../../hooks/useLocale';
import Storage from '../../utils/storage';
import { UitkButtonSize, UitkTertiaryButton } from 'uitk-react-button';

interface SurveyTriggerProps {
  timeBeforeDisplaying?: number;
}

// Survey trigger will appear again after 45 days are passed
const COOL_OFF_TIME = 45 * 24 * 60 * 60 * 1000;
const DEFAULT_TIME_BEFORE_DISPLAYING = 3 * 1000;

const SurveyTrigger = ({ timeBeforeDisplaying = DEFAULT_TIME_BEFORE_DISPLAYING }: SurveyTriggerProps) => {
  const { formatContent } = useLocale();
  const [showTrigger, setShowTrigger] = useState(hasSurveyBeenDisplayed() && !hasSurveyBeenDismissed());
  const nodeRef = useRef(null);

  useEffect(() => {
    if (hasSurveyBeenDismissed()) {
      return;
    }

    if (hasSurveyBeenDisplayed()) {
      setShowTrigger(true);
      return;
    }

    const timeout = setTimeout(() => {
      setShowTrigger(true);
      setHasSurveyBeenDisplayed();
    }, timeBeforeDisplaying);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const dismissTrigger = () => {
    setShowTrigger(false);
    setHasSurveyBeenDismissed();
  };

  return (
    <div className="survey">
      <CSSTransition in={showTrigger} nodeRef={nodeRef} timeout={300} classNames="trigger" unmountOnExit>
        <div ref={nodeRef}>
          <UitkCard backgroundTheme="secondary" padded>
            <UitkLayoutFlex space="two" alignItems="center" justifyContent="space-between">
              <UitkLayoutFlexItem>
                <div>
                  <UitkHeading>
                    {formatContent('survey.message.heading', 'Help us improve your experience')}
                  </UitkHeading>
                  <UitkSpacing padding={{ blockstart: 'one' }}>
                    <div>
                      <UitkLink onClick={() => dismissTrigger()} size="large">
                        <a
                          href="https://expediagroup.az1.qualtrics.com/jfe/form/SV_6tISQK4scDnz3ng"
                          target="_black"
                          rel="noreferrer"
                        >
                          {formatContent('survey.message.trigger.label', 'Take this 3 minute survey')}
                        </a>
                      </UitkLink>
                    </div>
                  </UitkSpacing>
                </div>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>
                <UitkTertiaryButton size={UitkButtonSize.SMALL} onClick={() => dismissTrigger()}>
                  <UitkIcon name="close" id="close" size={UitkIconSize.EXTRA_SMALL} theme="secondary" />
                </UitkTertiaryButton>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkCard>
        </div>
      </CSSTransition>
    </div>
  );
};

const hasSurveyBeenDismissed = () => Storage.getItem('hasSurveyBeenDismissed') === 'true';
const hasSurveyBeenDisplayed = () => Storage.getItem('hasSurveyBeenDisplayed') === 'true';
const setHasSurveyBeenDismissed = () => Storage.setItem('hasSurveyBeenDismissed', 'true', COOL_OFF_TIME);
const setHasSurveyBeenDisplayed = () => Storage.setItem('hasSurveyBeenDisplayed', 'true', COOL_OFF_TIME);

export default SurveyTrigger;
